// src/pages/AdminBlogPage.jsx
import React, { useEffect, useState } from "react";
import { adminListBlogs, adminCreateBlog, adminUpdateBlog, adminDeleteBlog } from "../services/BlogService";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

export default function AdminBlogPage() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  // form state
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [mediaFile, setMediaFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  const load = async (p = 1) => {
    try {
      setLoading(true);
      const data = await adminListBlogs({ page: p, limit: 20, q });
      setRows(data.rows || []);
      setPage(data.page || p);
      setPages(data.pages || 1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  function resetForm() {
    setEditing(null); setTitle(""); setExcerpt(""); setContent(""); setStatus("DRAFT");
    setMediaFile(null); setPreviewSrc(null);
  }

  const startEdit = (b) => {
    setEditing(b);
    setTitle(b.title); setExcerpt(b.excerpt || ""); setContent(b.content || "");
    setStatus(b.status || "DRAFT");
    setPreviewSrc(b.mediaUrl || null);
    setMediaFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onFile = (e) => {
    const f = e.target.files[0];
    setMediaFile(f);
    setPreviewSrc(f ? URL.createObjectURL(f) : null);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!title || !content) return toast.error("Title + content required");
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("excerpt", excerpt);
      fd.append("content", content);
      fd.append("status", status);
      if (mediaFile) fd.append("media", mediaFile);
      if (!editing) {
        await adminCreateBlog(fd);
        toast.success("Created");
      } else {
        await adminUpdateBlog(editing.id, fd);
        toast.success("Updated");
      }
      resetForm();
      load();
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      await adminDeleteBlog(id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="container py-4">
      <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="card mb-4 p-3">
        <h4>{editing ? "Edit Blog" : "Create Blog"}</h4>
        <form onSubmit={submit}>
          <input className="form-control mb-2" placeholder="Title" value={title} onChange={e=>setTitle(e.target.value)} />
          <input className="form-control mb-2" placeholder="Excerpt" value={excerpt} onChange={e=>setExcerpt(e.target.value)} />
          <textarea className="form-control mb-2" placeholder="Content (HTML allowed)" rows={8} value={content} onChange={e=>setContent(e.target.value)} />
          <select className="form-select mb-2" value={status} onChange={e=>setStatus(e.target.value)}>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Publish</option>
          </select>

          <div className="mb-2">
            <label className="form-label">Image / Video</label>
            <input type="file" accept="image/*,video/*" className="form-control" onChange={onFile} />
            {previewSrc && (
              <div className="mt-2">
                {/\.(mp4|webm|ogg)$/i.test(previewSrc) ? (
                  <video src={previewSrc} controls style={{maxWidth:300}} />
                ) : (
                  <img src={previewSrc} alt="preview" style={{maxWidth:300}} />
                )}
              </div>
            )}
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-primary" type="submit">{editing ? "Update" : "Create"}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Clear</button>
          </div>
        </form>
      </motion.div>

      <div className="card p-3">
        <h4>All Blogs</h4>
        <div className="mb-2 d-flex gap-2">
          <input className="form-control" placeholder="Search title/excerpt" value={q} onChange={e=>setQ(e.target.value)} />
          <button className="btn btn-primary" onClick={()=>load(1)}>Search</button>
        </div>

        <div className="list-group">
          {rows.map(b => (
            <div className="d-flex align-items-center justify-content-between list-group-item" key={b.id}>
              <div style={{display:"flex",gap:12, alignItems:"center"}}>
                {b.mediaUrl ? (/\.(mp4|webm|ogg)$/i.test(b.mediaUrl) ? <video src={b.mediaUrl} width={120} /> : <img src={b.mediaUrl} width={120} alt="" />) : <div style={{width:120,height:70,background:"#eee"}}/>}
                <div>
                  <div className="fw-bold">{b.title} {b.status === "DRAFT" && <small className="text-muted"> (draft)</small>}</div>
                  <div className="text-muted small">{b.excerpt}</div>
                </div>
              </div>
              <div>
                <button className="btn btn-sm btn-outline-primary me-2" onClick={()=>startEdit(b)}>Edit</button>
                <button className="btn btn-sm btn-outline-danger" onClick={()=>doDelete(b.id)}>Delete</button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-3 text-center">No blogs yet</div>}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>Page {page} / {pages}</div>
          <div>
            <button className="btn btn-sm btn-outline-secondary me-2" disabled={page<=1} onClick={()=>load(page-1)}>Prev</button>
            <button className="btn btn-sm btn-outline-secondary" disabled={page>=pages} onClick={()=>load(page+1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
